
import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import LoadingScreen from '../components/LoadingScreen';
import { csrfFetch } from '../contexts/csrfFetch';

const RAW_AUTH = process.env.REACT_APP_FN_BASE || '/auth';
const AUTH_BASE = RAW_AUTH.endsWith('/auth') ? RAW_AUTH : `${RAW_AUTH.replace(/\/$/, '')}/auth`;
const RAW_DATA = process.env.REACT_APP_DATA_BASE || '/.netlify/functions/api';
const DATA_BASE = RAW_DATA.endsWith('/api') ? RAW_DATA : `${RAW_DATA.replace(/\/$/, '')}/api`;
const API_AUTH = (p) => `${AUTH_BASE}${p.startsWith('/') ? p : `/${p}`}`;
const API_DATA = (p) => `${DATA_BASE}${p.startsWith('/') ? p : `/${p}`}`;

const ProfilePage = () => {
  const { user, signOut, logout, updateProfile } = useAuth();
  const navigate = useNavigate();

  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [info, setInfo] = useState('');

  const [deletingId, setDeletingId] = useState(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [pendingDeleteItem, setPendingDeleteItem] = useState(null);
  const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const [profileData, setProfileData] = useState({
    name: '',
    username: '',
    email: '',
    specialization: '',
    batch: '',
    role: ''
  });

  function timeAgoClient(iso, opts = {}) {
    const { isModified = false, now: nowOverride } = opts;
    try {
      if (!iso) return '';
      const then = new Date(iso);
      const now = nowOverride instanceof Date ? nowOverride : new Date();
      const diffMs = now - then;
      if (!isFinite(diffMs)) return '';
      const differenceInMinutes = Math.floor(diffMs / 60000);
      if (differenceInMinutes < 0) return '';
      let timeText = '';
      if (differenceInMinutes < 1) {
        timeText = 'الآن';
      } else if (differenceInMinutes < 60) {
        if (differenceInMinutes === 1) {
          timeText = 'قبل دقيقة';
        } else if (differenceInMinutes === 2) {
          timeText = 'قبل دقيقتين';
        } else if (differenceInMinutes <= 10) {
          timeText = `قبل ${differenceInMinutes} دقايق`;
        } else {
          timeText = `قبل ${differenceInMinutes} دقيقة`;
        }
      } else {
        const differenceInHours = Math.floor(differenceInMinutes / 60);
        const differenceInDays = Math.floor(differenceInHours / 24);
        const differenceInMonths = Math.floor(differenceInDays / 30);
        const differenceInYears = Math.floor(differenceInDays / 365);
        if (differenceInHours < 24) {
          if (differenceInHours === 1) {
            timeText = 'قبل ساعة';
          } else if (differenceInHours === 2) {
            timeText = 'قبل ساعتين';
          } else if (differenceInHours <= 10) {
            timeText = `قبل ${differenceInHours} ساعات`;
          } else {
            timeText = `قبل ${differenceInHours} ساعة`;
          }
        } else if (differenceInDays < 31) {
          if (differenceInDays === 1) {
            timeText = 'قبل يوم';
          } else if (differenceInDays === 2) {
            timeText = 'قبل يومين';
          } else if (differenceInDays <= 10) {
            timeText = `قبل ${differenceInDays} أيام`;
          } else {
            timeText = `قبل ${differenceInDays} يوم`;
          }
        } else if (differenceInMonths < 12) {
          if (differenceInMonths === 1) {
            timeText = 'قبل شهر';
          } else if (differenceInMonths === 2) {
            timeText = 'قبل شهرين';
          } else if (differenceInMonths <= 10) {
            timeText = `قبل ${differenceInMonths} شهور`;
          } else {
            timeText = `قبل ${differenceInMonths} شهر`;
          }
        } else {
          if (differenceInYears === 1) {
            timeText = 'مضى سنة';
          } else if (differenceInYears === 2) {
            timeText = 'مضى سنتين';
          } else if (differenceInYears <= 10) {
            timeText = `مضى ${differenceInYears} سنوات`;
          } else {
            timeText = 'مضى أكثر من 10 سنوات';
          }
        }
      }
      if (isModified) timeText = `معدلة: ${timeText}`;
      return timeText;
    } catch {
      return '';
    }
  }

  const [stats, setStats] = useState({
    reviewsCount: 0,
    commentsCount: 0,
    lecturersCount: 0,
    coursesCount: 0,
    lastReviewAt: '',
    lastCommentAt: '',
    recentReviews: [],
    byCategory: { explanation: 0, grades: 0, attendance: 0, interaction: 0 }
  });
  const [activityCounts, setActivityCounts] = useState({
    lecturerReviews: 0,
    courseReviews: 0,
    contributions: 0
  });

  const canSeeDashboard = useMemo(() => {
    const r = (user?.role && String(user.role)) || profileData.role || '';
    const rl = String(r).toLowerCase();
    return rl === 'admin' || rl === 'owner' || rl === 'developer' || !!user?.isAdmin || !!user?.isOwner;
  }, [user, profileData.role]);

  const canSeeDashboardOwner = useMemo(() => {
    const r = (user?.role && String(user.role)) || profileData.role || '';
    const rl = String(r).toLowerCase();
    return rl === 'developer' || rl === 'owner' || !!user?.isAdmin || !!user?.isOwner;
  }, [user, profileData.role]);

  const myEmail = useMemo(() => (profileData.email || user?.email || '').trim(), [profileData.email, user]);

  useEffect(() => {
    if (!user) return;
    setProfileData((p) => ({
      ...p,
      name: user.displayName || user.name || '',
      username: user.username || '',
      email: user.email || '',
      specialization: user.specialization || '',
      batch: user.batch || '',
      role: (user.role && String(user.role)) || ''
    }));
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({ ...prev, [name]: value }));
  };

  const fetchMe = async () => {
    try {
      const res = await fetch(API_AUTH('/me'), { credentials: 'include' });
      const data = await res.json();
      if (res.ok && data?.user) {
        setProfileData((p) => ({
          ...p,
          name: data.user.displayName || data.user.name || '',
          username: data.user.username || '',
          email: data.user.email || '',
          specialization: data.user.specialization || '',
          batch: data.user.batch || '',
          role: (data.user.role && String(data.user.role)) || ''
        }));
      }
    } catch {}
  };

  const fetchStats = async (email) => {
    try {
      if (!email) return;
      const r = await fetch(API_DATA(`/user/stats?email=${encodeURIComponent(email)}`), { credentials: 'include' });
      if (!r.ok) return;
      const s = await r.json();
      setStats((old) => ({
        ...old,
        commentsCount: s.commentsCount ?? 0,
        lastReviewAt: s.lastReviewAt || '',
        lastCommentAt: s.lastCommentAt || '',
        byCategory: s.byCategory || { explanation: 0, grades: 0, attendance: 0, interaction: 0 }
      }));
    } catch {}
  };

  const fetchRecentReviews = async (email) => {
    try {
      if (!email) return;
      let r = await fetch(API_DATA('/user/activity?limit=50'), { credentials: 'include' });
      if (r.status >= 500) {
        r = await fetch(API_DATA('/user/activity?limit=50'), { credentials: 'include' });
      }
      if (!r.ok) return;
      const payload = await r.json();
      const items = Array.isArray(payload?.items) ? payload.items : [];
      const counts = payload?.counts || {};
      setActivityCounts({
        lecturerReviews: Number(counts.lecturerReviews || 0),
        courseReviews: Number(counts.courseReviews || 0),
        contributions: Number(counts.contributions || 0)
      });
      setStats((old) => ({
        ...old,
        reviewsCount: Number(counts.lecturerReviews || 0) + Number(counts.courseReviews || 0),
        recentReviews: items
      }));
    } catch {}
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await fetchMe();
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (!myEmail) return;
      await Promise.all([fetchStats(myEmail), fetchRecentReviews(myEmail)]);
    })();
  }, [myEmail]);

  const handleEdit = async () => {
    setErr('');
    setInfo('');
    if (!isEditing) {
      setIsEditing(true);
      return;
    }
    try {
      setSaving(true);
      await updateProfile({
        name: profileData.name || '',
        specialization: profileData.specialization || '',
        batch: profileData.batch || ''
      });
      setIsEditing(false);
      await fetchMe();
      if (myEmail) {
        await Promise.all([fetchStats(myEmail), fetchRecentReviews(myEmail)]);
      }
      setInfo('تم حفظ التعديلات بنجاح');
      setTimeout(() => setInfo(''), 3000);
    } catch (e) {
      console.error('profile update error:', e);
      setErr(e?.message || 'تعذّر حفظ البيانات');
    } finally {
      setSaving(false);
    }
  };

  const makeSlug = (val) => {
    if (!val) return '';
    const clean = String(val).trim().replace(/\s+/g, '-');
    return encodeURIComponent(clean);
  };

  const resolveKind = (item) => {
    if (item?.kind) return item.kind;
    if (item?.course || item?.courseSlug) return 'course';
    if (item?.doctorSlug || item?.doctorName || item?.doctor || item?.lecturer) return 'doctor';
    return 'doctor';
  };

  const handleView = (item) => {
    if (!item) return;
    const kind = resolveKind(item);
    if (kind === 'explanation') {
      navigate(`/explanations?explanation=${encodeURIComponent(item.id)}`, {
        state: { fromProfile: true, explanationId: item.id }
      });
      return;
    }
    const base = kind === 'course' ? '/course-reviews' : '/doctor-reviews';
    const rawSlug =
      item.doctorSlug ||
      item.courseSlug ||
      item.slug ||
      item.title ||
      item.course ||
      item.doctorName ||
      item.doctor ||
      item.lecturer;
    const slug = makeSlug(rawSlug);
    if (!slug) return;
    const query = new URLSearchParams();
    if (item.id) query.set('review', String(item.id));
    query.set('name', String(rawSlug));
    const url = `${base}/${slug}?${query.toString()}`;
    navigate(url, {
      replace: false,
      state: { fromProfile: true, reviewId: item.id ?? null, kind }
    });
  };

  const sinceText = (s) => s || '—';

  const getReviewId = (itemOrId) => {
    if (!itemOrId) return null;
    if (typeof itemOrId === 'string' || typeof itemOrId === 'number') return itemOrId;
    return itemOrId.id || itemOrId._id || null;
  };

  const removeFromStateAfterDelete = (item) => {
    const rid = getReviewId(item);
    const kind = resolveKind(item);
    setStats((prev) => {
      const filtered = (prev.recentReviews || []).filter(
        (entry) => getReviewId(entry) !== rid || resolveKind(entry) !== kind
      );
      return {
        ...prev,
        recentReviews: filtered,
        reviewsCount: kind === 'explanation'
          ? prev.reviewsCount
          : Math.max(0, (prev.reviewsCount || 0) - 1)
      };
    });
  };

  const deleteActivityCall = async (item) => {
    try {
      const kind = resolveKind(item);
      const res = await csrfFetch(
        API_DATA(`/user/activity/${encodeURIComponent(kind)}/${encodeURIComponent(getReviewId(item))}`),
        { method: 'DELETE' }
      );
      if (res.ok) return { ok: true };
      const payload = await res.json().catch(() => ({}));
      return { ok: false, message: payload?.message || 'تعذر حذف العنصر' };
    } catch {
      return { ok: false, message: 'تعذر الاتصال بالخادم' };
    }
  };

  const askDelete = (item) => {
    setPendingDeleteItem(item);
    setIsConfirmOpen(true);
  };

  const confirmDelete = async () => {
    const item = pendingDeleteItem;
    const rid = getReviewId(item);
    if (!rid) {
      setErr('تعذر تحديد العنصر المراد حذفه');
      setIsConfirmOpen(false);
      setPendingDeleteItem(null);
      return;
    }
    try {
      setDeletingId(rid);
      const result = await deleteActivityCall(item);
      if (!result.ok) {
        setErr(result.message || 'تعذر حذف العنصر');
      } else {
        removeFromStateAfterDelete(item);
        if (myEmail) {
          await Promise.all([fetchStats(myEmail), fetchRecentReviews(myEmail)]);
        }
        setInfo('تم الحذف بنجاح');
        setTimeout(() => setInfo(''), 3000);
      }
    } catch {
      setErr('حدث خطأ غير متوقع أثناء الحذف');
    } finally {
      setDeletingId(null);
      setIsConfirmOpen(false);
      setPendingDeleteItem(null);
    }
  };

  const cancelDelete = () => {
    setIsConfirmOpen(false);
    setPendingDeleteItem(null);
  };

  const openLogoutConfirm = () => setIsLogoutConfirmOpen(true);
  const cancelLogout = () => setIsLogoutConfirmOpen(false);

  const confirmLogout = async () => {
    setErr('');
    setInfo('');
    setLoggingOut(true);
    try {
      if (typeof signOut === 'function') {
        await signOut();
      } else if (typeof logout === 'function') {
        await logout();
      } else {
        await csrfFetch(API_AUTH('/logout'), { method: 'POST' }).catch(() => {});
      }
    } catch (e) {
    } finally {
      setLoggingOut(false);
      setIsLogoutConfirmOpen(false);
      navigate('/', { replace: true });
    }
  };

  if (loading) {
    return (
      <LoadingScreen
        message="جارِ تحميل ملفك الشخصي…"
      />
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-6 text-white" style={{ paddingTop: '3rem' }} dir="rtl">
      <div className="max-w-4xl mx-auto px-2 sm:px-4 md:px-0 mt-10 md:mt-0">
        <div
          className="relative mb-8 w-full max-w-6xl mx-auto"
          style={{
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '8px',
            gap: '8px',
            isolation: 'isolate',
            minHeight: 'min(628px,80vh)',
            background:
              'linear-gradient(0deg, rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.02)), radial-gradient(92.12% 184.55% at 50% -55%, rgba(40, 227, 218, 0.2) 0%, rgba(40, 227, 218, 0) 100%), #081423',
            border: '0.924351px solid rgba(40, 227, 218, 0.25)',
            boxShadow: '0px 8px 32px rgba(255, 255, 255, 0.08)',
            backdropFilter: 'blur(1px)',
            borderRadius: '24px'
          }}
        >
          <div
            className="w-full h-full p-4 sm:p-6"
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              alignItems: 'stretch',
              gap: '17px',
              zIndex: 0
            }}
          >
            <div
              className="w-full flex-1"
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                padding: '0px',
                gap: '17px',
                flexGrow: 1
              }}
            >
              <div
                className="w-full"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  padding: '0px',
                  alignSelf: 'stretch'
                }}
              >
                <div
                  className="w-full"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    padding: '0px',
                    alignSelf: 'stretch',
                    margin: '-20px 0px'
                  }}
                >
                  <div
                    className="w-full"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'flex-end',
                      padding: '0px',
                      gap: '16px',
                      marginBottom: '28px',
                      marginTop: '10px'
                    }}
                  >
                    <div
                      className="w-full"
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '0px',
                        gap: '12px',
                        alignSelf: 'stretch',
                        flexWrap: 'wrap'
                      }}
                    >
                      <div
                        style={{
                          height: '16px',
                          fontFamily: 'Alexandria',
                          fontStyle: 'normal',
                          fontWeight: 800,
                          fontSize: '15px',
                          lineHeight: '100%',
                          textAlign: 'center',
                          color: '#FFFFFF'
                        }}
                      >
                        الملف الشخصي
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'center',
                          alignItems: 'center',
                          padding: '0px',
                          gap: '12px',
                          width: '50px',
                          height: '50px'
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'center',
                            alignItems: 'center',
                            padding: '16px',
                            gap: '8px',
                            width: '43px',
                            height: '43px',
                            background:
                              'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40, 227, 218, 0.5) 0%, rgba(40, 227, 218, 0) 100%), rgba(17, 101, 97, 0.25)',
                            boxShadow: '0px 8px 32px rgba(255, 255, 255, 0.08)',
                            backdropFilter: 'blur(1px)',
                            borderRadius: '100px'
                          }}
                        >
                          <svg
                            width="14"
                            height="20"
                            viewBox="0 0 17 23"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            style={{ width: '14px', height: '20px' }}
                          >
                            <path
                              d="M15.5001 4.5L8.50006 1.5L1.50006 4.5L5.00006 6V8M15.5001 4.5L12.0001 6V8M15.5001 4.5V8.5M5.00006 8C5.00006 8 6.16706 7.5 8.50006 7.5C10.8331 7.5 12.0001 8 12.0001 8M5.00006 8V9C5.00006 9.45963 5.09059 9.91475 5.26648 10.3394C5.44237 10.764 5.70018 11.1499 6.02519 11.4749C6.35019 11.7999 6.73603 12.0577 7.16067 12.2336C7.58531 12.4095 8.04043 12.5 8.50006 12.5C8.95969 12.5 9.41481 12.4095 9.83945 12.2336C10.2641 12.0577 10.6499 11.7999 10.9749 11.4749C11.2999 11.1499 11.5577 10.764 11.7336 10.3394C11.9095 9.91475 12.0001 9.45963 12.0001 9V8M4.28306 16.203C3.18306 16.888 0.297061 18.285 2.05406 20.034C2.91306 20.89 3.87006 21.5 5.07106 21.5H11.9291C13.1311 21.5 14.0871 20.889 14.9461 20.034C16.7031 18.285 13.8181 16.888 12.7171 16.204"
                              stroke="#FFFFFF"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className="w-full"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    padding: '0px'
                  }}
                >
                  <div
                    className="w-full"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      padding: '0px',
                      gap: '11px',
                      alignSelf: 'stretch'
                    }}
                  >
                    <div
                      style={{
                        width: '100%',
                        maxWidth: '820px',
                        height: '30px',
                        fontFamily: 'Alexandria',
                        fontStyle: 'normal',
                        fontWeight: 800,
                        fontSize: '14px',
                        lineHeight: '215%',
                        textAlign: 'right',
                        color: '#FFFFFF'
                      }}
                    >
                      الاسم
                    </div>
                    <div
                      className="w-full"
                      style={{
                        height: '57px',
                        background: '#12334A',
                        borderRadius: '16px',
                        alignSelf: 'stretch'
                      }}
                    >
                      <input
                        type="text"
                        name="name"
                        value={profileData.name}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className="w-full h-full px-4 sm:px-6 text-white bg-transparent border-none outline-none text-right"
                        style={{ fontFamily: 'Alexandria', fontSize: '14px' }}
                      />
                    </div>

                    <div
                      style={{
                        width: '100%',
                        maxWidth: '820px',
                        height: '30px',
                        fontFamily: 'Alexandria',
                        fontStyle: 'normal',
                        fontWeight: 800,
                        fontSize: '14px',
                        lineHeight: '215%',
                        textAlign: 'right',
                        color: '#FFFFFF'
                      }}
                    >
                      اسم المستخدم
                    </div>
                    <div
                      className="w-full"
                      style={{
                        height: '57px',
                        background: '#12334A',
                        borderRadius: '16px',
                        alignSelf: 'stretch'
                      }}
                    >
                      <input
                        type="text"
                        name="username"
                        value={profileData.username}
                        onChange={handleInputChange}
                        disabled={true}
                        className="w-full h-full px-4 sm:px-6 text-white bg-transparent border-none outline-none text-right"
                        style={{ fontFamily: 'Alexandria', fontSize: '14px' }}
                      />
                    </div>

                    <div
                      style={{
                        width: '100%',
                        maxWidth: '820px',
                        height: '30px',
                        fontFamily: 'Alexandria',
                        fontStyle: 'normal',
                        fontWeight: 800,
                        fontSize: '14px',
                        lineHeight: '215%',
                        textAlign: 'right',
                        color: '#FFFFFF'
                      }}
                    >
                      البريد الالكتروني
                    </div>
                    <div
                      className="w-full"
                      style={{
                        height: '57px',
                        background: '#12334A',
                        borderRadius: '16px',
                        alignSelf: 'stretch'
                      }}
                    >
                      <input
                        type="email"
                        name="email"
                        value={profileData.email}
                        onChange={handleInputChange}
                        disabled={true}
                        className="w-full h-full px-4 sm:px-6 text-white bg-transparent border-none outline-none text-right"
                        style={{ fontFamily: 'Alexandria', fontSize: '14px' }}
                      />
                    </div>

                    <div
                      className="w-full flex flex-col md:flex-row gap-4 md:gap-6"
                      style={{ height: 'auto', alignSelf: 'stretch' }}
                    >
                      <div className="w-full md:w-1/2">
                        <div
                          className="mb-2 text-right"
                          style={{
                            fontFamily: 'Alexandria',
                            fontStyle: 'normal',
                            fontWeight: 800,
                            fontSize: '14px',
                            lineHeight: '215%',
                            color: '#FFFFFF'
                          }}
                        >
                          الدفعة (اختياري)
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            padding: '16px 18px',
                            gap: '8px',
                            minHeight: '57px',
                            background: '#12334A',
                            borderRadius: '16px'
                          }}
                        >
                          <input
                            type="text"
                            name="batch"
                            value={profileData.batch}
                            onChange={handleInputChange}
                            disabled={!isEditing}
                            className="w-full h-full text-white bg-transparent border-none outline-none text-right"
                            style={{ fontFamily: 'Alexandria', fontSize: '14px' }}
                          />
                        </div>
                      </div>

                      <div className="w-full md:w-1/2">
                        <div
                          className="mb-2 text-right"
                          style={{
                            fontFamily: 'Alexandria',
                            fontStyle: 'normal',
                            fontWeight: 800,
                            fontSize: '14px',
                            lineHeight: '215%',
                            color: '#FFFFFF'
                          }}
                        >
                          التخصص (اختياري)
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            padding: '16px 18px',
                            gap: '8px',
                            minHeight: '57px',
                            background: '#12334A',
                            borderRadius: '16px'
                          }}
                        >
                          <input
                            type="text"
                            name="specialization"
                            value={profileData.specialization}
                            onChange={handleInputChange}
                            disabled={!isEditing}
                            className="w-full h-full text-white bg-transparent border-none outline-none text-right"
                            style={{ fontFamily: 'Alexandria', fontSize: '14px' }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className="w-full mt-auto"
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '0px',
                    gap: '16px',
                    height: '55px',
                    alignSelf: 'stretch',
                    marginTop: '24px'
                  }}
                >
                  <div style={{ width: '165px', height: '55px' }}>
                    <button
                      onClick={handleEdit}
                      disabled={saving}
                      className="w-full h-full flex items-center justify-center disabled:opacity-60"
                      style={{
                        background:
                          'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40, 227, 218, 0.5) 0%, rgba(40, 227, 218, 0) 100%), rgba(40, 227, 218, 0.25)',
                        borderRadius: '10px',
                        border: 'none',
                        cursor: 'pointer',
                        fontFamily: 'Alexandria',
                        fontStyle: 'normal',
                        fontWeight: 600,
                        fontSize: '15px',
                        lineHeight: '120%',
                        textAlign: 'center',
                        color: '#FFFFFF'
                      }}
                    >
                      {isEditing ? (saving ? 'جارِ الحفظ…' : 'حفظ') : 'تعديل'}
                    </button>
                  </div>
                </div>

                {err ? (
                  <div className="w-full text-center text-red-400 mt-2" style={{ fontFamily: 'Alexandria' }}>
                    {err}
                  </div>
                ) : null}
                {info ? (
                  <div className="w-full text-center text-emerald-300 mt-2" style={{ fontFamily: 'Alexandria' }}>
                    {info}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-8" dir="rtl">
          <div
            className="w-full"
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'stretch',
              padding: '16px',
              gap: '12px',
              minHeight: '146px',
              background:
                'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40, 227, 218, 0.25) 0%, rgba(40, 227, 218, 0) 100%), #0b1c2b',
              boxShadow: '0px 10px 40px rgba(0,0,0,.25)',
              borderRadius: '24px',
              border: '1px solid rgba(40, 227, 218, 0.25)'
            }}
          >
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(40,227,218,.20)' }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <path d="M3 3h18v14a2 2 0 0 1-2 2H9l-6 4V3z" stroke="white" strokeWidth="1.5" />
                    <path d="M7 9v6M12 5v10M17 11v4" stroke="white" strokeWidth="1.5" />
                  </svg>
                </div>
                <div className="flex h-10 min-w-10 items-center justify-center rounded-xl border border-white/10 bg-white/[0.07] px-2 text-xl font-extrabold text-white sm:h-11 sm:min-w-11" style={{ fontFamily: 'Alexandria' }}>
                  {activityCounts.lecturerReviews}
                </div>
                <div className="text-sm font-semibold leading-6 text-white/80" style={{ fontFamily: 'Alexandria', textAlign: 'right' }}>
                  تقييمات المحاضرين
                </div>
              </div>
              <span
                className="px-3 py-1 rounded-full text-xs text-white/90 text-right"
                style={{ background: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,255,255,.15)', fontFamily: 'Alexandria' }}
                title={stats.lastReviewAt || '—'}
                dir="rtl"
              >
                آخر نشاط: {timeAgoClient(stats.lastReviewAt) || '—'}
              </span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {[
                { label: 'شرح', val: stats.byCategory?.explanation || 0 },
                { label: 'درجات', val: stats.byCategory?.grades || 0 },
                { label: 'تحضير', val: stats.byCategory?.attendance || 0 },
                { label: 'تعامل', val: stats.byCategory?.interaction || 0 }
              ].map((c) => (
                <div
                  key={c.label}
                  className="rounded-xl px-3 py-2 text-center transition"
                  style={{ background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.10)' }}
                >
                  <div className="text-xs text-white/70" style={{ fontFamily: 'Alexandria' }}>{c.label}</div>
                  <div className="text-lg font-bold text-white" style={{ fontFamily: 'Alexandria' }}>{c.val}</div>
                </div>
              ))}
            </div>
          </div>

          <div
            className="w-full"
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'stretch',
              padding: '16px',
              gap: '12px',
              minHeight: '146px',
              background:
                'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40, 227, 218, 0.25) 0%, rgba(40, 227, 218, 0) 100%), #0b1c2b',
              boxShadow: '0px 10px 40px rgba(0,0,0,.25)',
              borderRadius: '24px',
              border: '1px solid rgba(40, 227, 218, 0.25)'
            }}
          >
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(40,227,218,.20)' }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <path d="M21 15a2 2 0 0 1-2 2H8l-5 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10Z" stroke="white" strokeWidth="1.5" />
                  </svg>
                </div>
                <div className="flex h-10 min-w-10 items-center justify-center rounded-xl border border-white/10 bg-white/[0.07] px-2 text-xl font-extrabold text-white sm:h-11 sm:min-w-11" style={{ fontFamily: 'Alexandria' }}>
                  {activityCounts.courseReviews}
                </div>
                <div className="text-sm font-semibold leading-6 text-white/80" style={{ fontFamily: 'Alexandria', textAlign: 'right' }}>
                  تقييمات المقررات
                </div>
              </div>
              <span
                className="px-3 py-1 rounded-full text-xs text-white/90 text-right"
                style={{ background: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,255,255,.15)', fontFamily: 'Alexandria' }}
                title={stats.lastCommentAt || '—'}
                dir="rtl"
              >
                سجل التقييمات
              </span>
            </div>

            <div
              className="rounded-xl px-4 py-3"
              style={{ background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.10)' }}
            >
              <div className="text-xs text-white/70 mb-1" style={{ fontFamily: 'Alexandria' }}>آخر مقرر قيّمته</div>
              <div className="text-sm text-white line-clamp-2" style={{ fontFamily: 'Alexandria' }}>
                {(Array.isArray(stats.recentReviews) && stats.recentReviews.find(r => r.kind === 'course')?.title) || 'لا يوجد حتى الآن'}
              </div>
            </div>
          </div>

          <div
            className="w-full"
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'stretch',
              padding: '16px',
              gap: '12px',
              minHeight: '146px',
              background:
                'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40, 227, 218, 0.25) 0%, rgba(40, 227, 218, 0) 100%), #0b1c2b',
              boxShadow: '0px 10px 40px rgba(0,0,0,.25)',
              borderRadius: '24px',
              border: '1px solid rgba(40, 227, 218, 0.25)'
            }}
          >
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(40,227,218,.20)' }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20V4H6.5A2.5 2.5 0 0 0 4 6.5v13Z" stroke="white" strokeWidth="1.5" />
                    <path d="M8 8h8M8 12h6" stroke="white" strokeWidth="1.5" />
                  </svg>
                </div>
                <div className="flex h-10 min-w-10 items-center justify-center rounded-xl border border-white/10 bg-white/[0.07] px-2 text-xl font-extrabold text-white sm:h-11 sm:min-w-11" style={{ fontFamily: 'Alexandria' }}>
                  {activityCounts.contributions}
                </div>
                <div className="text-sm font-semibold leading-6 text-white/80" style={{ fontFamily: 'Alexandria', textAlign: 'right' }}>
                  المساهمات التعليمية
                </div>
              </div>
              <span className="px-3 py-1 rounded-full text-xs text-white/90" style={{ background: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,255,255,.15)', fontFamily: 'Alexandria' }}>
                شروحات وملخصات
              </span>
            </div>
            <div className="rounded-xl px-4 py-3" style={{ background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.10)' }}>
              <div className="text-xs text-white/70 mb-1" style={{ fontFamily: 'Alexandria' }}>آخر مساهمة</div>
              <div className="text-sm text-white line-clamp-2" style={{ fontFamily: 'Alexandria' }}>
                {(Array.isArray(stats.recentReviews) && stats.recentReviews.find(r => r.kind === 'explanation')?.title) || 'لا توجد مساهمات حتى الآن'}
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4 w-full" dir="rtl">
          {Array.isArray(stats.recentReviews) && stats.recentReviews.length > 0 ? (
            stats.recentReviews.map((item, idx) => {
              const rid = getReviewId(item) ?? idx;
              const isDeleting = deletingId === getReviewId(item);
              const kind = resolveKind(item);
              const typeLabel = kind === 'course'
                ? 'تقييم مقرر'
                : kind === 'explanation'
                  ? 'مساهمة تعليمية'
                  : 'تقييم محاضر';
              const statusLabel = item.status === 'approved'
                ? 'منشورة'
                : item.status === 'rejected'
                  ? 'مرفوضة'
                  : 'قيد المراجعة';
              return (
                <div
                  key={`${kind}-${rid}`}
                  style={{
                    padding: '18px',
                    background:
                      'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40, 227, 218, 0.25) 0%, rgba(40, 227, 218, 0) 100%), #0b1c2b',
                    borderRadius: '24px',
                    border: '1px solid rgba(40, 227, 218, 0.25)',
                    boxShadow: '0px 10px 40px rgba(0,0,0,.25)'
                  }}
                >
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div className="flex flex-wrap items-center justify-start gap-2">
                      <span className="rounded-full px-2.5 py-1 text-[11px] text-[#7cf6ef]" style={{ background: 'rgba(40,227,218,.10)', border: '1px solid rgba(40,227,218,.22)', fontFamily: 'Alexandria' }}>
                        {typeLabel}
                      </span>
                      {kind === 'explanation' && (
                        <span className="text-[11px] text-white/55" style={{ fontFamily: 'Alexandria' }}>{statusLabel}</span>
                      )}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <span
                        className="shrink-0 rounded-full px-3 py-1 text-right text-xs text-white/90"
                        style={{ background: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,255,255,.15)', fontFamily: 'Alexandria' }}
                        title={item.createdAt || item.timestamp || '—'}
                        dir="rtl"
                      >
                        منذ: {timeAgoClient(item.createdAt) || item.timestamp || '—'}
                      </span>
                      <button
                        onClick={() => askDelete(item)}
                        disabled={isDeleting}
                        className="w-8 h-8 rounded-xl flex items-center justify-center hover:bg-red-500/20 transition-colors disabled:opacity-50"
                        style={{ background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.12)' }}
                        aria-label="حذف"
                        title={isDeleting ? 'جاري الحذف…' : 'حذف'}
                      >
                        {isDeleting ? (
                          <svg viewBox="0 0 24 24" width="16" height="16" className="animate-spin">
                            <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="2" fill="none" opacity="0.3" />
                            <path d="M22 12a10 10 0 0 0-10-10" stroke="white" strokeWidth="2" fill="none" />
                          </svg>
                        ) : (
                          <svg width="16" height="16" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M10.5 31.5C9.675 31.5 8.969 31.2065 8.382 30.6195C7.795 30.0325 7.50101 29.326 7.50001 28.5V9C7.07501 9 6.71901 8.856 6.43201 8.568C6.14501 8.28 6.00101 7.924 6.00001 7.5C5.99901 7.076 6.14301 6.72 6.43201 6.432C6.72101 6.144 7.07701 6 7.50001 6H13.5C13.5 5.575 13.644 5.219 13.932 4.932C14.22 4.645 14.576 4.501 15 4.5H21C21.425 4.5 21.7815 4.644 22.0695 4.932C22.3575 5.22 22.501 5.576 22.5 6H28.5C28.925 6 29.2815 6.144 29.5695 6.432C29.8575 6.72 30.001 7.076 30 7.5C29.999 7.924 29.855 8.2805 29.568 8.5695C29.281 8.8585 28.925 9.002 28.5 9V28.5C28.5 29.325 28.2065 30.0315 27.6195 30.6195C27.0325 31.2075 26.326 31.501 25.5 31.5H10.5ZM25.5 9H10.5V28.5H25.5V9ZM15 25.5C15.425 25.5 15.7815 25.356 16.0695 25.068C16.3575 24.78 16.501 24.424 16.5 24V13.5C16.5 13.075 16.356 12.719 16.068 12.432C15.78 12.145 15.424 12.001 15 12C14.576 11.999 14.22 12.143 13.932 12.432C13.644 12.721 13.5 13.077 13.5 13.5V24C13.5 24.425 13.644 24.7815 13.932 25.0695C14.22 25.3575 14.576 25.501 15 25.5ZM21 25.5C21.425 25.5 21.7815 25.356 22.0695 25.068C22.3575 24.78 22.501 24.424 22.5 24V13.5C22.5 13.075 22.356 12.719 22.068 12.432C21.78 12.145 21.424 12.001 21 12C20.576 11.999 20.22 12.143 19.932 12.432C19.644 12.721 19.5 13.077 19.5 13.5V24C19.5 24.425 19.644 24.7815 19.932 25.0695C20.22 25.3575 20.576 25.501 21 25.5Z" fill="white" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="mb-4 block w-full" dir="rtl" style={{ direction: 'rtl', textAlign: 'right' }}>
                    <div className="w-full text-base font-semibold leading-7 text-white" style={{ fontFamily: 'Alexandria', textAlign: 'right' }}>
                      {item.title || item.doctorName || item.doctor || item.lecturer || item.course}
                    </div>
                    {item.subtitle && <div className="mt-1 w-full text-xs text-white/55" style={{ fontFamily: 'Alexandria', textAlign: 'right' }}>{item.subtitle}</div>}
                  </div>

                  <div className="space-y-2" style={{ direction: 'rtl' }}>
                    <div
                      className="rounded-xl px-4 py-3"
                      style={{ background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.10)' }}
                    >
                      <div className="text-xs text-white/70 mb-1" style={{ fontFamily: 'Alexandria' }}>
                        {kind === 'explanation' ? 'نبذة عن المساهمة' : 'رأيك'}
                      </div>
                      <div className="text-sm text-white leading-6 line-clamp-3" style={{ fontFamily: 'Alexandria' }}>
                        {item.comment || 'لم تُضف ملاحظة مع هذا التقييم'}
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => handleView(item)}
                    className="w-full mt-4 transition-all duration-200"
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'center',
                      alignItems: 'center',
                      padding: '12px 34px',
                      gap: '8px',
                      width: '100%',
                      background:
                        'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40, 227, 218, 0.5) 0%, rgba(40, 227, 218, 0) 100%), rgba(40, 227, 218, 0.25)',
                      borderRadius: '16px',
                      border: 'none',
                      fontFamily: 'Alexandria',
                      fontStyle: 'normal',
                      fontWeight: '400',
                      fontSize: '16px',
                      lineHeight: '100%',
                      textAlign: 'right',
                      color: '#EEEEEE'
                    }}
                  >
                    فتح التفاصيل
                  </button>
                </div>
              );
            })
          ) : (
            <div className="text-center text-white/70" style={{ fontFamily: 'Alexandria' }}>
              لا توجد عناصر حديثة
            </div>
          )}
        </div>

        <div className="flex flex-col items-center gap-4 mt-8 mb-6 w-full">
          {canSeeDashboard && (
            <button
              onClick={() => navigate('/dashboard')}
              className="transition-all duration-200 w-full max-w-xs"
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '12px 36px',
                gap: '8px',
                background:
                  'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40, 227, 218, 0.5) 0%, rgba(40, 227, 218, 0) 100%), rgba(40, 227, 218, 0.25)',
                borderRadius: '16px',
                border: 'none',
                fontFamily: 'Alexandria',
                fontStyle: 'normal',
                fontWeight: '400',
                fontSize: '16px',
                lineHeight: '100%',
                textAlign: 'center',
                color: '#FFFFFF',
                cursor: 'pointer'
              }}
            >
              لوحة تحكم الادمن
            </button>
          )}

          {canSeeDashboard && (
            <button
              onClick={() => navigate('/popup-settings')}
              className="transition-all duration-200 w-full max-w-xs"
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '12px 36px',
                gap: '8px',
                background:
                  'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40, 227, 218, 0.3) 0%, rgba(40, 227, 218, 0) 100%), rgba(40, 227, 218, 0.15)',
                borderRadius: '16px',
                border: '1px solid rgba(40,227,218,0.3)',
                fontFamily: 'Alexandria',
                fontStyle: 'normal',
                fontWeight: '400',
                fontSize: '16px',
                lineHeight: '100%',
                textAlign: 'center',
                color: '#FFFFFF',
                cursor: 'pointer'
              }}
            >
              إعدادات النافذة المنبثقة
            </button>
          )}

          {canSeeDashboardOwner && (
            <button
              onClick={() => navigate('/maintenance-control')}
              className="transition-all duration-200 w-full max-w-xs"
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '12px 36px',
                gap: '8px',
                background:
                  'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40, 227, 218, 0.5) 0%, rgba(40, 227, 218, 0) 100%), rgba(40, 227, 218, 0.25)',
                borderRadius: '16px',
                border: 'none',
                fontFamily: 'Alexandria',
                fontStyle: 'normal',
                fontWeight: '400',
                fontSize: '16px',
                lineHeight: '100%',
                textAlign: 'center',
                color: '#FFFFFF',
                cursor: 'pointer'
              }}
            >
              لوحة تحكم المالك
            </button>
          )}

          <button
            onClick={openLogoutConfirm}
            disabled={loggingOut}
            className="transition-all duration-200 w-full max-w-xs disabled:opacity-60"
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '12px 36px',
              gap: '8px',
              background:
                'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(255, 77, 77, 0.15) 0%, rgba(40, 227, 218, 0) 100%), rgba(255, 77, 77, 0.15)',
              borderRadius: '16px',
              fontFamily: 'Alexandria',
              fontSize: '16px',
              color: '#fff',
              cursor: 'pointer'
            }}
            title={loggingOut ? 'جاري تسجيل الخروج…' : 'تسجيل الخروج'}
          >
            {loggingOut ? 'جاري تسجيل الخروج…' : 'تسجيل الخروج'}
          </button>
        </div>
      </div>

      {isConfirmOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          style={{
            background: 'rgba(0,0,0,0.55)',
            backdropFilter: 'blur(2px)'
          }}
          dir="rtl"
        >
          <div
            className="w-full max-w-md"
            style={{
              background:
                'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40, 227, 218, 0.25) 0%, rgba(40, 227, 218, 0) 100%), #0b1c2b',
              borderRadius: '20px',
              border: '1px solid rgba(40, 227, 218, 0.25)',
              boxShadow: '0px 10px 40px rgba(0,0,0,.35)'
            }}
            role="dialog"
            aria-modal="true"
          >
            <div className="p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.12)' }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <path d="M3 6h18" stroke="white" strokeWidth="1.5" />
                    <path d="M8 6V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v1" stroke="white" strokeWidth="1.5" />
                    <path d="M6 6v13a3 3 0 0 0 3 3h6a3 3 0 0 0 3-3V6" stroke="white" strokeWidth="1.5" />
                    <path d="M10 11v6M14 11v6" stroke="white" strokeWidth="1.5" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Alexandria' }}>تأكيد الحذف</h3>
              </div>

              <p className="text-white/90 mb-5" style={{ fontFamily: 'Alexandria' }}>
                هل تريد حذف هذا العنصر نهائيًا؟ لا يمكن التراجع بعد الحذف.
              </p>

              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={cancelDelete}
                  className="px-4 h-10 rounded-xl"
                  style={{
                    background: 'rgba(255,255,255,.06)',
                    border: '1px solid rgba(255,255,255,.12)',
                    color: '#fff',
                    fontFamily: 'Alexandria'
                  }}
                >
                  إلغاء
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-4 h-10 rounded-xl"
                  style={{
                    background:
                      'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40, 227, 218, 0.5) 0%, rgba(40, 227, 218, 0) 100%), rgba(40, 227, 218, 0.25)',
                    border: 'none',
                    color: '#fff',
                    fontFamily: 'Alexandria'
                  }}
                >
                  حذف
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isLogoutConfirmOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)' }}
          dir="rtl"
        >
          <div
            className="w-full max-w-md"
            style={{
              background:
                'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40, 227, 218, 0.25) 0%, rgba(40, 227, 218, 0) 100%), #0b1c2b',
              borderRadius: '20px',
              border: '1px solid rgba(40, 227, 218, 0.25)',
              boxShadow: '0px 10px 40px rgba(0,0,0,.35)'
            }}
            role="dialog"
            aria-modal="true"
          >
            <div className="p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.12)' }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <path d="M16 17l5-5-5-5M21 12H9" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                    <path d="M12 21a9 9 0 1 1 0-18" stroke="white" strokeWidth="1.5" opacity=".6" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Alexandria' }}>تأكيد تسجيل الخروج</h3>
              </div>

              <p className="text-white/90 mb-5" style={{ fontFamily: 'Alexandria' }}>
                هل تريد بالتأكيد تسجيل الخروج من حسابك؟
              </p>

              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={cancelLogout}
                  disabled={loggingOut}
                  className="px-4 h-10 rounded-xl disabled:opacity-60"
                  style={{
                    background: 'rgba(255,255,255,.06)',
                    border: '1px solid rgba(255,255,255,.12)',
                    color: '#fff',
                    fontFamily: 'Alexandria'
                  }}
                >
                  إلغاء
                </button>
                <button
                  onClick={confirmLogout}
                  disabled={loggingOut}
                  className="px-4 h-10 rounded-xl disabled:opacity-60"
                  style={{
                    background:
                      'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40, 227, 218, 0.5) 0%, rgba(40, 227, 218, 0) 100%), rgba(40, 227, 218, 0.25)',
                    border: 'none',
                    color: '#fff',
                    fontFamily: 'Alexandria',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  {loggingOut ? (
                    <>
                      <svg viewBox="0 0 24 24" width="16" height="16" className="animate-spin">
                        <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="2" fill="none" opacity="0.3" />
                        <path d="M22 12a10 10 0 0 0-10-10" stroke="white" strokeWidth="2" fill="none" />
                      </svg>
                      جاري تسجيل الخروج…
                    </>
                  ) : (
                    'تسجيل الخروج'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
